import { useDraggable } from '@dnd-kit/core';
import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Clock3 } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import { parseLocalDate } from '@/utils/date';
import { formatTimelineDuration } from '@/utils/timeline';

export interface TimelineCardEntry {
  id: string;
  title: string;
  projectName: string;
  projectColor: string | null;
  durationMinutes: number;
  /** Inclusive number of timeline day columns occupied by this card. */
  spanDays?: number;
  startDate?: string;
  endDate?: string | null;
}

interface TimelineCardProps {
  entry: TimelineCardEntry;
  /** Vertical offset inside the day cell when multiple cards stack. */
  stackIndex: number;
  rowHeight: number;
  stackGap: number;
  onClick: (id: string) => void;
  onContextMenu: (id: string, x: number, y: number) => void;
}

const DEFAULT_COLOR = '#6366f1';

function formatCardDate(date: string): string {
  return format(parseLocalDate(date), 'MMM d, yyyy');
}

function formatCardDateRange(startDate?: string, endDate?: string | null): string | null {
  if (!startDate) return null;
  const start = formatCardDate(startDate);
  if (!endDate || endDate < startDate || endDate === startDate) return start;
  return `${start} – ${formatCardDate(endDate)}`;
}

function getCardTooltip(entry: TimelineCardEntry, duration: string): string {
  const dateRange = formatCardDateRange(entry.startDate, entry.endDate);
  return [
    `Task: ${entry.title}`,
    `Project: ${entry.projectName}`,
    `Duration: ${duration}`,
    dateRange ? `Dates: ${dateRange}` : null,
  ]
    .filter((line): line is string => Boolean(line))
    .join('\n');
}

/**
 * One Time Entry rendered as a Toggl-style colored block. Acts as the drag
 * source for `useDragToMove` and as the click/right-click target for opening
 * the edit dialog or context menu.
 */
export function TimelineCard({
  entry,
  stackIndex,
  rowHeight,
  stackGap,
  onClick,
  onContextMenu,
}: TimelineCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: entry.id,
      data: { entryId: entry.id },
    });

  // Suppress the synthetic click that the browser fires after a successful
  // drag — but ONLY when there was actual movement. With `distance: 0` on
  // the PointerSensor, every pointerDown is also a "drag", so we can't
  // blanket-suppress click after isDragging; instead we look at the live
  // transform and flag the click only when the card actually moved.
  const justMovedRef = useRef(false);
  useEffect(() => {
    if (!transform) return;
    if (transform.x === 0 && transform.y === 0) return;
    justMovedRef.current = true;
    const timeout = setTimeout(() => {
      justMovedRef.current = false;
    }, 100);
    return () => clearTimeout(timeout);
  }, [transform]);

  const backgroundColor = entry.projectColor ?? DEFAULT_COLOR;
  const spanDays = Math.max(1, entry.spanDays ?? 1);
  const isMultiDay = spanDays > 1;
  const duration = formatTimelineDuration(entry.durationMinutes);
  const dateRange = formatCardDateRange(entry.startDate, entry.endDate);
  const tooltip = getCardTooltip(entry, duration);
  const accessibleLabel = [
    `Task: ${entry.title}`,
    `Project: ${entry.projectName}`,
    `Duration: ${duration}`,
    dateRange ? `Dates: ${dateRange}` : null,
  ]
    .filter((line): line is string => Boolean(line))
    .join('. ');

  return (
    <Tooltip content={tooltip}>
      <div
        ref={setNodeRef}
        data-testid="timeline-item"
        data-entry-id={entry.id}
        data-span-days={spanDays}
        data-start-date={entry.startDate}
        data-end-date={entry.endDate ?? undefined}
        aria-label={accessibleLabel}
        className={`absolute left-1 overflow-visible rounded-sm text-white shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${
          isMultiDay ? 'right-auto' : 'right-1'
        }`}
        style={{
          top: stackIndex * (rowHeight + stackGap),
          height: rowHeight,
          width: isMultiDay ? `calc(${spanDays * 100}% - 0.5rem)` : undefined,
          backgroundColor,
          opacity: isDragging ? 0.85 : 1,
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
            : undefined,
          zIndex: isDragging ? 30 : 10,
          cursor: isDragging ? 'grabbing' : 'grab',
          boxShadow: isDragging
            ? '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
            : '0 1px 2px rgba(0, 0, 0, 0.08)',
          touchAction: 'none',
        }}
        onClick={(event) => {
          if (justMovedRef.current) return;
          event.stopPropagation();
          onClick(entry.id);
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onContextMenu(entry.id, event.clientX, event.clientY);
        }}
        {...listeners}
        {...attributes}
      >
        <div className="flex h-full min-w-0 flex-col justify-center overflow-hidden rounded-sm px-2 py-1.5">
          <span
            data-testid="timeline-item-title"
            className="truncate text-xs font-semibold leading-tight"
          >
            {entry.title}
          </span>
          <span
            data-testid="timeline-item-meta"
            className="flex min-w-0 items-center gap-1 text-[10px] leading-tight opacity-90"
          >
            <span
              data-testid="timeline-item-project"
              className="min-w-0 flex-1 truncate"
            >
              {entry.projectName}
            </span>
            <span
              data-testid="timeline-item-duration"
              className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap"
            >
              <Clock3 className="h-3 w-3" aria-hidden="true" />
              <span>{duration}</span>
            </span>
          </span>
        </div>
      </div>
    </Tooltip>
  );
}
